import { useCallback, useEffect, useState } from "react"

export type TActiveNavLink = {
  id: string | number
  title: string
  url: string
}

const getDocumentHeight = () => {
  return Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight
  )
}

export const useActiveNavLink = (data: TActiveNavLink[], offsetTop = 0) => {
  const [activeId, setActiveId] = useState<string | number | null>(null)

  const handleScroll = useCallback(() => {
    const isMainPage = window.location.pathname === "/"
    if (!isMainPage) return setActiveId(null)

    const scrollHeight = getDocumentHeight() - window.innerHeight
    const isBottomDocument = window.scrollY >= scrollHeight

    if (isBottomDocument && data[data.length - 1].url.includes("#"))
      return setActiveId(data[data.length - 1].id)

    const currentItem = data.find(item => {
      const isHashLink = item.url.includes("#")
      if (!isHashLink) return false

      const hashId = item.url.split("#")[1]
      const element = document.getElementById(hashId)
      if (!element) return false

      const rect = element.getBoundingClientRect()
      const isInViewport =
        rect.top + offsetTop <= 0 && rect.bottom + offsetTop >= 0

      return isInViewport
    })

    setActiveId(currentItem ? currentItem.id : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)])

  const scrollToElement = useCallback(
    (elementId: string) => {
      const element = document.getElementById(elementId)
      if (!element) return

      window.scrollTo({
        top:
          element.getBoundingClientRect().top + window.scrollY + 1 + offsetTop, // +1 to make sure the element is in the viewport
        left: 0,
        behavior: "smooth"
      })
    },
    [offsetTop]
  )

  useEffect(() => {
    handleScroll()
    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [handleScroll])

  return { activeId, scrollToElement }
}
